import {Badge, Box, Button, MantineProvider, Paper, Text} from '@mantine/core';
import type {NextRouter} from 'next/router';
import type React from 'react';
import {useState} from 'react';
import {getAIGradient} from '../../usecase/getAIGradient';

type Props = {
  router: NextRouter;
  companyID: number;
  departments?: Array<{departmentID: number; departmentName: string}>;
};

const badgeStylesDefault = {
  root: {
    borderImageSource: getAIGradient(),
    borderImageSlice: 1,
    background: getAIGradient(),
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    transition: 'all 0.5s ease-in-out',
    opacity: 0.8,
    cursor: 'pointer',
    '&:hover': {
      opacity: 1,
    },
  },
};

const badgeStylesActive = {
  root: {
    background: getAIGradient(),
    color: 'white',
    transition: 'all 0.5s ease-in-out',
    opacity: 1,
    cursor: 'pointer',
  },
};

type badgeProps = {
  itemID: number;
  item: string;
  isActive: boolean;
  setActive: (id: number) => void;
};

const AiBadge: React.FC<badgeProps> = ({itemID, item, isActive, setActive}) => {
  // IsActiveとsetActiveを追加
  return (
    <MantineProvider
      theme={{
        components: {
          Badge: {
            styles: isActive ? badgeStylesActive : badgeStylesDefault, // IsActiveに基づいてスタイルを切り替え
          },
        },
      }}
    >
      <Badge
        size="lg"
        radius="sm"
        onClick={() => {
          setActive(itemID);
        }}
      >
        {item}
      </Badge>
    </MantineProvider>
  );
};

export const AnalyticsSidebar: React.FC<Props> = ({
  router,
  companyID,
  departments,
}) => {
  const {reportType} = router.query as {reportType: string};
  const [activeBadge, setActiveBadge] = useState<number | undefined>(undefined);

  return (
    <>
      <Box className="w-full bg-white p-12">
        <Box className="w-full flex mt-12">
          <Text
            className="text-center text-md font-bold"
            style={{
              background: getAIGradient(),
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            この企業の他の職種・部門
          </Text>
        </Box>
        <Box className="flex flex-wrap justify-start items-center gap-8 py-16">
          {departments && departments.length > 0 ? (
            departments.map((department) => (
              <AiBadge
                key={department.departmentID}
                itemID={department.departmentID}
                item={department.departmentName}
                isActive={activeBadge === department.departmentID}
                setActive={(id: number | undefined) => {
                  setActiveBadge(id);
                }}
              />
            ))
          ) : (
            <Text className="text-black-2 text-sm">
              他の職種・部門のデータがありません。
            </Text>
          )}
        </Box>
        <Box>
          <Paper
            className="border-2"
            style={{
              borderImageSource: getAIGradient(),
              borderImageSlice: 1,
              background: getAIGradient(),
            }}
          />
        </Box>
        <Box className="w-full flex mt-12 justify-center p-4">
          {departments && departments.length > 0 ? (
            <Box className="flex flex-col gap-16">
              <Button
                radius="xl"
                size="xl"
                className="text-md"
                style={{
                  background:
                    activeBadge === undefined
                      ? 'defaultBackground'
                      : getAIGradient(),
                }}
                fullWidth
                disabled={activeBadge === undefined}
                onClick={() => {
                  if (activeBadge !== undefined) {
                    window.location.href = `/report_analytics/result/${companyID}/${activeBadge}?reportType=${reportType}`;
                  }
                }}
              >
                選択した職種・部門で分析
              </Button>

              <button
                className="text-md font-bold p-16 bg-white border-2 border-transparent active:bg-line"
                style={{
                  borderImage: `${getAIGradient()} 1`,
                  borderImageSlice: 1,
                }}
                onClick={() => {
                  window.location.href = '/report_analytics/search';
                }}
              >
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    background: getAIGradient(),
                    WebkitBackgroundClip: 'text',
                  }}
                >
                  他の企業で分析
                </span>
              </button>
            </Box>
          ) : (
            <Button
              radius="xl"
              size="xl"
              className="text-md"
              style={{
                background: getAIGradient(),
              }}
              fullWidth
              onClick={() => {
                window.location.href = '/report_analytics/search';
              }}
            >
              他の企業で分析
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};
